import React, { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { formatRelativeTime } from '../../utils/format'

// ── Gradient helper (same pattern as siteGradient) ────────────────────────────
const REQ_GRADIENTS = [
  ['#7C5CFF', '#5B7CFF'],
  ['#A78BFA', '#7C5CFF'],
  ['#EC4899', '#A78BFA'],
  ['#FF8A65', '#F76C5E'],
  ['#2BD9A6', '#06B6D4'],
  ['#06B6D4', '#5B7CFF'],
  ['#FFB454', '#FF8A65'],
  ['#5BC0EB', '#5B7CFF'],
]

function reqGradient(id) {
  const hash = (id || '').toString().split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  return REQ_GRADIENTS[hash % REQ_GRADIENTS.length]
}

function getInitials(name) {
  if (!name) return '?'
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
}

// ── Priority & status maps ────────────────────────────────────────────────────
const PRI_TONE = { high: 'danger', urgent: 'danger', medium: 'warning', normal: 'warning', low: 'info' }
const PRI_LABEL = { high: 'High', urgent: 'Urgent', medium: 'Medium', normal: 'Normal', low: 'Low' }

const STATUS_CONFIG = {
  open:        { label: 'Open',        tone: 'warning' },
  pending:     { label: 'Open',        tone: 'warning' },
  'in-progress': { label: 'In Progress', tone: 'info' },
  approved:    { label: 'In Progress', tone: 'info' },
  'in_progress': { label: 'In Progress', tone: 'info' },
  'in-review': { label: 'In Review',   tone: 'accent' },
  completed:   { label: 'Completed',   tone: 'success' },
  rejected:    { label: 'Rejected',    tone: 'danger' },
}

const KANBAN_COLS = [
  { id: 'open',        label: 'Open',        tone: 'warning',  statuses: ['open', 'pending'] },
  { id: 'in-progress', label: 'In Progress', tone: 'info',     statuses: ['in-progress', 'approved', 'in_progress'] },
  { id: 'in-review',   label: 'In Review',   tone: 'accent',   statuses: ['in-review'] },
  { id: 'completed',   label: 'Completed',   tone: 'success',  statuses: ['completed'] },
]

// ── Avatar component ──────────────────────────────────────────────────────────
function Avatar({ name, id, size = 26, style: extraStyle = {} }) {
  const [from, to] = reqGradient(id || name)
  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.35, flexShrink: 0,
      background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: Math.max(8, size * 0.36),
      ...extraStyle,
    }}>
      {getInitials(name)}
    </div>
  )
}

// ── Kanban card ───────────────────────────────────────────────────────────────
function KanbanCard({ request, onClick }) {
  const priTone = PRI_TONE[request.priority] || 'warning'
  const priLabel = PRI_LABEL[request.priority] || request.priority || '—'
  const clientName = request.company_name || request.client_name || '—'
  const desc = request.description || request.desc || ''

  return (
    <div className="kanban-card" onClick={() => onClick(request)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Avatar name={clientName} id={request.client_id || request.id} size={22} />
        <span style={{ fontSize: 11.5, color: 'var(--text-muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {clientName}
        </span>
        <span className={`badge badge-${priTone}`} style={{ fontSize: 10 }}>{priLabel}</span>
      </div>

      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, lineHeight: 1.35 }}>
        {request.title}
      </div>

      {desc && (
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {desc}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
        {request.assigned_to_name ? (
          <Avatar name={request.assigned_to_name} id={request.assigned_to} size={20} />
        ) : (
          <div style={{ width: 20, height: 20, borderRadius: 7, background: 'var(--surface)', border: '1.5px dashed var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 9 }}>?</div>
        )}
        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          {request.assigned_to_name || 'Unassigned'}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>
          {request.created_at ? formatRelativeTime(request.created_at) : '—'}
        </span>
      </div>
    </div>
  )
}

// ── Request drawer ────────────────────────────────────────────────────────────
function RequestDrawer({ requestId, onClose, onUpdated }) {
  const queryClient = useQueryClient()
  const [reply, setReply] = useState('')

  const { data: request, isLoading } = useQuery(
    ['request-detail', requestId],
    () => apiService.get(`/requests/${requestId}`).then(res => res.data),
    { enabled: !!requestId }
  )

  // Status update mutation
  const updateStatusMutation = useMutation(
    (newStatus) => apiService.post(`/requests/${requestId}/approve`, { status: newStatus }),
    {
      onSuccess: () => {
        toast.success('Status updated')
        queryClient.invalidateQueries(['request-detail', requestId])
        queryClient.invalidateQueries('requests')
        onUpdated()
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to update status')
    }
  )

  // Approve mutation (preserved from original)
  const approveMutation = useMutation(
    ({ notes, estimatedHours, assignedTo, deadline }) =>
      apiService.post(`/requests/${requestId}/approve`, {
        approval_notes: notes,
        estimated_hours: estimatedHours,
        assigned_to: assignedTo,
        deadline,
      }),
    {
      onSuccess: () => {
        toast.success('Request approved!')
        queryClient.invalidateQueries(['request-detail', requestId])
        onUpdated()
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to approve')
    }
  )

  // Reject mutation (preserved from original)
  const rejectMutation = useMutation(
    (reason) => apiService.post(`/requests/${requestId}/reject`, { rejection_reason: reason }),
    {
      onSuccess: () => {
        toast.success('Request rejected!')
        queryClient.invalidateQueries(['request-detail', requestId])
        onUpdated()
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to reject')
    }
  )

  // Complete mutation (preserved from original)
  const completeMutation = useMutation(
    () => apiService.post(`/requests/${requestId}/complete`),
    {
      onSuccess: () => {
        toast.success('Request marked as completed!')
        queryClient.invalidateQueries(['request-detail', requestId])
        onUpdated()
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to complete request')
    }
  )

  // Delete mutation (preserved from original)
  const deleteMutation = useMutation(
    () => apiService.delete(`/requests/${requestId}`),
    {
      onSuccess: () => {
        toast.success('Request deleted')
        queryClient.invalidateQueries('requests')
        onUpdated()
        onClose()
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to delete request')
    }
  )

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const clientName = request?.company_name || request?.client_name || '—'
  const status = request?.status || 'open'
  const priTone = PRI_TONE[request?.priority] || 'warning'
  const priLabel = PRI_LABEL[request?.priority] || request?.priority || '—'
  const statusCfg = STATUS_CONFIG[status] || { label: status, tone: 'warning' }

  const handleStatusClick = (newStatus) => {
    if (newStatus === 'completed') completeMutation.mutate()
    else if (newStatus === 'in-progress' || newStatus === 'approved') approveMutation.mutate({})
    else if (newStatus === 'rejected') rejectMutation.mutate('')
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="request-drawer open">
        {/* Head */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-faint)' }}>
              {request?.id ? `REQ-${request.id}` : '…'}
            </span>
            {request?.priority && (
              <span className={`badge badge-${priTone}`} style={{ fontSize: 10 }}>{priLabel} priority</span>
            )}
            {request?.category && (
              <span className="badge" style={{ fontSize: 10 }}>{request.category}</span>
            )}
            <button
              className="site-action"
              style={{ marginLeft: 'auto' }}
              onClick={onClose}
              title="Close"
            >
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          {isLoading ? (
            <div style={{ height: 24, background: 'var(--surface-2)', borderRadius: 6, marginBottom: 10 }} />
          ) : (
            <h2 style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.3, marginBottom: 10 }}>
              {request?.title || 'Loading…'}
            </h2>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar name={clientName} id={request?.client_id} size={22} />
            <span style={{ fontSize: 13, color: 'var(--text-soft)' }}>{clientName}</span>
            {request?.site_name && (
              <>
                <span style={{ color: 'var(--text-faint)' }}>·</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{request.site_name}</span>
              </>
            )}
            {request?.created_at && (
              <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                {formatRelativeTime(request.created_at)}
              </span>
            )}
          </div>
        </div>

        {/* Body (scrollable) */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Status selector */}
          <div>
            <div style={{ fontSize: 10.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Status</div>
            <div className="segmented" style={{ width: '100%' }}>
              {KANBAN_COLS.map(col => (
                <button
                  key={col.id}
                  className={status === col.id || col.statuses.includes(status) ? 'on' : ''}
                  style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}
                  onClick={() => handleStatusClick(col.id)}
                  disabled={updateStatusMutation.isLoading || completeMutation.isLoading}
                >
                  {col.label}
                </button>
              ))}
            </div>
          </div>

          {/* Meta grid */}
          {request && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', background: 'var(--surface)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--border)' }}>
              {[
                { k: 'Client', v: clientName },
                { k: 'Requested by', v: request.created_by_name || '—' },
                { k: 'Priority', v: <span className={`badge badge-${priTone}`} style={{ fontSize: 10 }}>{priLabel}</span> },
                { k: 'Status', v: <span className={`badge badge-${statusCfg.tone}`} style={{ fontSize: 10 }}><span className="badge-dot" />{statusCfg.label}</span> },
              ].map(({ k, v }) => (
                <div key={k}>
                  <div style={{ fontSize: 10.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{k}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-soft)', display: 'flex', alignItems: 'center', gap: 6 }}>{v}</div>
                </div>
              ))}
            </div>
          )}

          {/* Description */}
          {request?.description && (
            <div>
              <div style={{ fontSize: 10.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Description</div>
              <div style={{ fontSize: 13.5, color: 'var(--text-soft)', lineHeight: 1.6, background: 'var(--surface)', borderRadius: 10, padding: '12px 14px', border: '1px solid var(--border)' }}>
                {request.description}
              </div>
            </div>
          )}

          {/* Quick actions */}
          {request && (
            <div style={{ display: 'flex', gap: 8 }}>
              {['pending', 'open'].includes(status) && (
                <>
                  <button className="btn btn-sm" style={{ flex: 1, background: 'var(--success)', color: '#fff', border: 'none' }} onClick={() => approveMutation.mutate({})} disabled={approveMutation.isLoading}>
                    Approve
                  </button>
                  <button className="btn btn-sm" style={{ flex: 1, background: 'var(--error)', color: '#fff', border: 'none' }} onClick={() => rejectMutation.mutate('')} disabled={rejectMutation.isLoading}>
                    Reject
                  </button>
                </>
              )}
              {['approved', 'in_progress', 'in-progress', 'in-review'].includes(status) && (
                <button className="btn btn-sm" style={{ flex: 1, background: 'var(--success)', color: '#fff', border: 'none' }} onClick={() => completeMutation.mutate()} disabled={completeMutation.isLoading}>
                  Mark Complete
                </button>
              )}
              <button
                className="btn btn-sm"
                style={{ color: 'var(--error)', flexShrink: 0 }}
                onClick={() => { if (window.confirm('Delete this request?')) deleteMutation.mutate() }}
                disabled={deleteMutation.isLoading}
                title="Delete request"
              >
                <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
              </button>
            </div>
          )}

          {/* Attachments placeholder */}
          {request?.attachments?.length > 0 && (
            <div>
              <div style={{ fontSize: 10.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Attachments · {request.attachments.length}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {request.attachments.map((a, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 12px', fontSize: 12.5 }}>
                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{a.name || a.filename || `File ${i + 1}`}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer reply composer */}
        <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            <textarea
              placeholder={`Reply to ${request?.created_by_name || 'client'}…`}
              value={reply}
              onChange={e => setReply(e.target.value)}
              style={{
                width: '100%', minHeight: 80, padding: '12px 14px',
                background: 'transparent', border: 'none', outline: 'none',
                fontSize: 13.5, color: 'var(--text)', lineHeight: 1.5,
                resize: 'none', fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderTop: '1px solid var(--border)' }}>
              <button className="btn btn-sm btn-ghost">
                <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ marginRight: 5 }}><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
                Attach
              </button>
              <button className="btn btn-sm btn-primary" style={{ marginLeft: 'auto' }} disabled={!reply.trim()}>
                <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ marginRight: 5 }}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                Send reply
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────────

const RequestManager = () => {
  const [filterStatus, setFilterStatus] = useState('')
  const [filterPriority, setFilterPriority] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedRequestId, setSelectedRequestId] = useState(null)
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // Auto-open a specific request when ?open=id is in the URL
  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) setSelectedRequestId(openId)
  }, [searchParams])

  // Debounce search
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(searchQuery), 400)
    return () => clearTimeout(h)
  }, [searchQuery])

  // ── API calls (preserved exactly) ──
  const { data: requests, isLoading } = useQuery(
    ['requests', filterStatus, filterPriority],
    () => apiService.get('/requests', {
      params: { status: filterStatus, priority: filterPriority }
    }).then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  )

  // Approve mutation (preserved from original)
  const approveMutation = useMutation(
    ({ requestId, notes, estimatedHours, deadline, assignedTo }) =>
      apiService.post(`/requests/${requestId}/approve`, {
        approval_notes: notes,
        estimated_hours: estimatedHours,
        deadline,
        assigned_to: assignedTo,
      }),
    {
      onSuccess: () => {
        toast.success('Request approved successfully!')
        queryClient.invalidateQueries('requests')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to approve request')
      }
    }
  )

  // Reject mutation (preserved from original)
  const rejectMutation = useMutation(
    ({ requestId, reason }) => apiService.post(`/requests/${requestId}/reject`, { rejection_reason: reason }),
    {
      onSuccess: () => {
        toast.success('Request rejected successfully!')
        queryClient.invalidateQueries('requests')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to reject request')
      }
    }
  )

  // Complete mutation (preserved from original)
  const completeMutation = useMutation(
    (requestId) => apiService.post(`/requests/${requestId}/complete`),
    {
      onSuccess: () => {
        toast.success('Request marked as completed!')
        queryClient.invalidateQueries('requests')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to complete request')
      }
    }
  )

  const displayRequests = requests || []

  const filtered = displayRequests.filter(r => {
    const q = debouncedSearch.toLowerCase()
    const matchesSearch = !q ||
      r.title?.toLowerCase().includes(q) ||
      r.description?.toLowerCase().includes(q) ||
      r.company_name?.toLowerCase().includes(q)
    const matchesPriority = !filterPriority || r.priority === filterPriority
    return matchesSearch && matchesPriority
  })

  // Build kanban columns
  const kanbanCols = KANBAN_COLS.map(col => ({
    ...col,
    items: filtered.filter(r => col.statuses.includes(r.status)),
  }))

  const totalOpen = filtered.filter(r => ['open', 'pending'].includes(r.status)).length
  const totalActive = filtered.filter(r => ['in-progress', 'approved', 'in_progress', 'in-review'].includes(r.status)).length

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Inbox</div>
          <h1>Requests</h1>
          <div className="subtitle">
            {totalOpen > 0 ? `${totalOpen} open` : 'No open requests'}
            {totalActive > 0 && ` · ${totalActive} in progress`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn">
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            Filters
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* Filter bar */}
        <div className="filter-bar">
          <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-muted)', flexShrink: 0 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            className="input"
            placeholder="Search requests…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <div className="vdiv" style={{ height: 22 }} />
          <div className="segmented">
            {[['', 'All'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low']].map(([val, label]) => (
              <button key={val} className={filterPriority === val ? 'on' : ''} onClick={() => setFilterPriority(val)}>
                {label}
              </button>
            ))}
          </div>
          <div className="vdiv" style={{ height: 22 }} />
          <div className="segmented" style={{ marginLeft: 'auto' }}>
            <button className={!filterStatus ? 'on' : ''} onClick={() => setFilterStatus('')}>Board</button>
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
            Loading requests…
          </div>
        )}

        {/* Kanban board */}
        {!isLoading && (
          <div className="kanban-board">
            {kanbanCols.map(col => (
              <div key={col.id} className="kanban-col">
                <div className="kanban-col-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span className={`badge badge-${col.tone}`} style={{ fontSize: 10 }}>
                      <span className="badge-dot" />{col.label}
                    </span>
                  </div>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>{col.items.length}</span>
                </div>

                {col.items.map(r => (
                  <KanbanCard key={r.id} request={r} onClick={() => setSelectedRequestId(r.id)} />
                ))}

                {col.items.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--text-faint)', border: '1.5px dashed var(--border)', borderRadius: 12 }}>
                    No requests
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Request detail drawer */}
      {selectedRequestId && (
        <RequestDrawer
          requestId={selectedRequestId}
          onClose={() => setSelectedRequestId(null)}
          onUpdated={() => queryClient.invalidateQueries('requests')}
        />
      )}
    </>
  )
}

export default RequestManager
